import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  GestureResponderEvent,
  LayoutChangeEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Dialog from 'components/dialog';
import { Typography } from 'components/ui';
import { ZIndex, primitive, spacing } from 'styles/constants';
import { breakpoints } from 'styles/constants/breakpoints';

import { copyDiagnosticMarkdown } from '../context/clipboard';
import {
  clampDiagnosticLauncherPosition,
  getDefaultDiagnosticLauncherPosition,
  isDiagnosticDrag,
} from '../context/position';
import { useDiagnosticContext, useDiagnosticToolsEnabled } from '../context/provider';
import { DiagnosticLauncherPosition } from '../context/types';

import { DiagnosticMarkdownPreview } from './diagnostic-markdown-preview';

const EDGE_MARGIN = 12;

type WebDragStyle = ViewStyle & { touchAction: 'none' };

const webDragStyle: WebDragStyle | undefined =
  Platform.OS === 'web' ? { cursor: 'pointer', touchAction: 'none' } : undefined;

const getGesturePoint = (event: GestureResponderEvent) => ({
  x: event.nativeEvent.pageX,
  y: event.nativeEvent.pageY,
});

export const DiagnosticContextSurface = ({ isModal = false }: { isModal?: boolean }) => {
  const enabled = useDiagnosticToolsEnabled();

  return enabled ? <EnabledDiagnosticContextSurface isModal={isModal} /> : null;
};

const EnabledDiagnosticContextSurface = ({ isModal }: { isModal: boolean }) => {
  const hostId = useId();
  const {
    activeHostId,
    close,
    isOpen,
    launcherPosition,
    markdown,
    open,
    refresh,
    registerHost,
    setLauncherPosition,
  } = useDiagnosticContext();
  const dimensions = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isMobileViewport = dimensions.width < breakpoints.md;
  const [hostSize, setHostSize] = useState({ width: 0, height: 0 });
  const [launcherSize, setLauncherSize] = useState({ width: 0, height: 0 });
  const [transientPosition, setTransientPosition] = useState<DiagnosticLauncherPosition | null>(
    null
  );
  const [copyFeedback, setCopyFeedback] = useState<string>();
  const gestureOriginRef = useRef<DiagnosticLauncherPosition>({ x: 0, y: 0 });
  const gestureStartRef = useRef<DiagnosticLauncherPosition>({ x: 0, y: 0 });
  const gestureDeltaRef = useRef<DiagnosticLauncherPosition>({ x: 0, y: 0 });
  const didDragRef = useRef(false);

  useEffect(() => registerHost(hostId, isModal), [hostId, isModal, registerHost]);

  const boundsInput = useMemo(
    () => ({
      viewportWidth: hostSize.width || dimensions.width,
      viewportHeight: hostSize.height || dimensions.height,
      launcherWidth: launcherSize.width,
      launcherHeight: launcherSize.height,
      insetTop: isModal ? 0 : insets.top,
      insetRight: insets.right,
      insetBottom: insets.bottom,
      insetLeft: insets.left,
      edgeMargin: EDGE_MARGIN,
    }),
    [dimensions.height, dimensions.width, hostSize, insets, isModal, launcherSize]
  );
  const defaultPosition = useMemo(() => getDefaultDiagnosticLauncherPosition(boundsInput), [
    boundsInput,
  ]);
  const effectivePosition = transientPosition ?? launcherPosition ?? defaultPosition;

  useEffect(() => {
    if (!launcherPosition || launcherSize.width === 0 || launcherSize.height === 0) {
      return;
    }
    const clamped = clampDiagnosticLauncherPosition(launcherPosition, boundsInput);
    if (clamped.x !== launcherPosition.x || clamped.y !== launcherPosition.y) {
      setLauncherPosition(clamped);
    }
  }, [boundsInput, launcherPosition, launcherSize, setLauncherPosition]);

  useEffect(() => {
    if (!isOpen) {
      setCopyFeedback(undefined);
    }
  }, [isOpen]);

  const getGestureDelta = (event: GestureResponderEvent): DiagnosticLauncherPosition => {
    const point = getGesturePoint(event);
    return {
      x: point.x - gestureStartRef.current.x,
      y: point.y - gestureStartRef.current.y,
    };
  };

  const getBoundedGesturePosition = (delta: DiagnosticLauncherPosition) =>
    clampDiagnosticLauncherPosition(
      {
        x: gestureOriginRef.current.x + delta.x,
        y: gestureOriginRef.current.y + delta.y,
      },
      boundsInput
    );

  const handleResponderGrant = (event: GestureResponderEvent) => {
    gestureOriginRef.current = effectivePosition;
    gestureStartRef.current = getGesturePoint(event);
    gestureDeltaRef.current = { x: 0, y: 0 };
    didDragRef.current = false;
  };

  const handleResponderMove = (event: GestureResponderEvent) => {
    const delta = getGestureDelta(event);
    gestureDeltaRef.current = delta;
    if (!didDragRef.current && isDiagnosticDrag(delta.x, delta.y)) {
      didDragRef.current = true;
    }
    if (didDragRef.current) {
      setTransientPosition(getBoundedGesturePosition(delta));
    }
  };

  const commitDrag = () => {
    setLauncherPosition(getBoundedGesturePosition(gestureDeltaRef.current));
    setTransientPosition(null);
  };

  const handleResponderRelease = (event: GestureResponderEvent) => {
    const delta = getGestureDelta(event);
    gestureDeltaRef.current = delta;
    if (didDragRef.current || isDiagnosticDrag(delta.x, delta.y)) {
      commitDrag();
      return;
    }
    setTransientPosition(null);
    open();
  };

  const handleResponderTerminate = () => {
    if (didDragRef.current) {
      commitDrag();
      return;
    }
    setTransientPosition(null);
  };

  const handleLauncherLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    if (width !== launcherSize.width || height !== launcherSize.height) {
      setLauncherSize({ width, height });
    }
  };

  const handleHostLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setHostSize(current =>
      current.width === width && current.height === height ? current : { width, height }
    );
  };

  const handleCopy = async () => {
    if (!markdown) {
      return;
    }
    const result = await copyDiagnosticMarkdown(markdown);
    setCopyFeedback(result.ok ? 'Copied Jira context.' : result.reason);
  };

  const handleRefresh = () => {
    setCopyFeedback(undefined);
    refresh();
  };

  if (activeHostId !== hostId) {
    return null;
  }

  return (
    <View
      onLayout={handleHostLayout}
      pointerEvents="box-none"
      style={styles.overlay}
      testID="diagnostic-context-host"
    >
      <View
        accessible
        accessibilityActions={[{ name: 'activate', label: 'Open diagnostic context' }]}
        accessibilityHint="Drag to reposition. Activate to capture current diagnostic context."
        accessibilityLabel="Jira diagnostic context"
        accessibilityRole="button"
        onAccessibilityAction={event => {
          if (event.nativeEvent.actionName === 'activate') {
            open();
          }
        }}
        onLayout={handleLauncherLayout}
        onMoveShouldSetResponder={() => true}
        onResponderGrant={handleResponderGrant}
        onResponderMove={handleResponderMove}
        onResponderRelease={handleResponderRelease}
        onResponderTerminate={handleResponderTerminate}
        onResponderTerminationRequest={() => false}
        onStartShouldSetResponder={() => true}
        onStartShouldSetResponderCapture={() => true}
        style={[
          styles.launcher,
          isMobileViewport ? styles.mobileLauncher : null,
          webDragStyle,
          {
            transform: [{ translateX: effectivePosition.x }, { translateY: effectivePosition.y }],
          },
        ]}
        testID="jira-diagnostic-context-launcher"
      >
        <Typography style={styles.launcherText}>
          {isMobileViewport ? 'QA' : 'QA Context'}
        </Typography>
      </View>

      <Dialog
        actions={
          <View style={styles.actions} testID="diagnostic-context-actions">
            <Pressable
              accessibilityLabel="Copy Jira context"
              accessibilityRole="button"
              onPress={handleCopy}
              style={[
                styles.actionButton,
                isMobileViewport ? styles.mobileActionButton : styles.desktopActionButton,
              ]}
              testID="diagnostic-context-copy"
            >
              <Typography style={styles.actionText}>
                {isMobileViewport ? 'Copy' : 'Copy Jira context'}
              </Typography>
            </Pressable>
            <Pressable
              accessibilityLabel="Refresh context"
              accessibilityRole="button"
              onPress={handleRefresh}
              style={[
                styles.actionButton,
                isMobileViewport ? styles.mobileActionButton : styles.desktopActionButton,
              ]}
              testID="diagnostic-context-refresh"
            >
              <Typography style={styles.actionText}>
                {isMobileViewport ? 'Refresh' : 'Refresh context'}
              </Typography>
            </Pressable>
            <Pressable
              accessibilityLabel="Close"
              accessibilityRole="button"
              onPress={close}
              style={[
                styles.closeButton,
                isMobileViewport ? styles.mobileActionButton : styles.desktopCloseButton,
              ]}
              testID="diagnostic-context-close"
            >
              <Typography style={styles.closeText}>Close</Typography>
            </Pressable>
          </View>
        }
        contentMaxWidth="760px"
        contentWidth="90%"
        heading="Jira diagnostic context"
        modalAppearanceEventMessage="Internal diagnostic context opened"
        onDismiss={close}
        showDialog={isOpen}
        testID="jira-diagnostic-context-dialog"
      >
        <View style={styles.dialogBody}>
          <ScrollView contentContainerStyle={styles.markdownContent} style={styles.markdownScroll}>
            <DiagnosticMarkdownPreview markdown={markdown ?? ''} />
          </ScrollView>
          {!!copyFeedback && (
            <Typography accessibilityLiveRegion="polite" style={styles.feedback}>
              {copyFeedback}
            </Typography>
          )}
        </View>
      </Dialog>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: ZIndex.TOP,
  },
  launcher: {
    position: 'absolute',
    left: 0,
    top: 0,
    minWidth: 92,
    minHeight: 44,
    paddingHorizontal: spacing.$3,
    paddingVertical: spacing.$2,
    borderRadius: primitive.$borderRadius,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: primitive.$bbqBrown,
    shadowColor: primitive.$black,
    shadowOpacity: 0.24,
    shadowRadius: 5,
    elevation: 8,
    userSelect: 'none',
  },
  launcherText: {
    color: primitive.$white,
    fontWeight: '700',
  },
  mobileLauncher: {
    minWidth: 44,
    paddingHorizontal: spacing.$2,
  },
  dialogBody: {
    paddingHorizontal: spacing.$4,
    gap: spacing.$2,
  },
  markdownScroll: {
    maxHeight: 420,
    borderWidth: 1,
    borderColor: primitive.$blackOpacity10,
    borderRadius: primitive.$borderRadius,
  },
  markdownContent: {
    padding: spacing.$3,
  },
  feedback: {
    color: primitive.$bbqBrown,
  },
  actions: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'flex-end',
    gap: spacing.$2,
    paddingTop: spacing.$3,
  },
  actionButton: {
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: primitive.$borderRadius,
    backgroundColor: primitive.$fieryRed,
  },
  actionText: {
    color: primitive.$white,
    fontWeight: '700',
    textAlign: 'center',
  },
  closeButton: {
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: primitive.$borderRadius,
    borderWidth: 1,
    borderColor: primitive.$bbqBrown,
  },
  closeText: {
    color: primitive.$bbqBrown,
    fontWeight: '700',
    textAlign: 'center',
  },
  mobileActionButton: {
    flex: 1,
    minWidth: 0,
    paddingHorizontal: spacing.$2,
  },
  desktopActionButton: {
    minWidth: 156,
    paddingHorizontal: spacing.$4,
  },
  desktopCloseButton: {
    minWidth: 112,
    paddingHorizontal: spacing.$4,
  },
});
